/**
 * EmployeeDocumentsTab — R148g · Feb 2026
 * ---------------------------------------
 * HR-friendly checklist for an employee's paperwork. Predefined rows
 * (ID proof · Address · Education · Past work · Certificates) come
 * seeded from the backend so HR just uploads and verifies. Extra
 * "Other" rows can be added ad-hoc.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FileText, Upload, CheckCircle2, X, Loader2, Plus,
  Paperclip, Calendar, ShieldCheck, ShieldAlert, AlertTriangle,
} from "lucide-react";

export default function EmployeeDocumentsTab({ wsId, userId }) {
  const [state, setState] = useState({ loading: true, rows: [], summary: null, categories: {} });
  const [busyRow, setBusyRow] = useState(null);
  const [addingOther, setAddingOther] = useState(false);
  const [otherTitle, setOtherTitle] = useState("");
  const uploadRefs = useRef({});

  const load = useCallback(async () => {
    if (!wsId || !userId) return;
    setState((p) => ({ ...p, loading: true }));
    try {
      const { data } = await api.get(`/workspaces/${wsId}/employee-documents`, {
        params: { user_id: userId },
      });
      setState({ loading: false, rows: data.rows || [], summary: data.summary || null, categories: data.categories || {} });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load documents"));
      setState((p) => ({ ...p, loading: false }));
    }
  }, [wsId, userId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (docId, body) => {
    setBusyRow(docId);
    try {
      await api.patch(`/workspaces/${wsId}/employee-documents/${docId}`, body);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
    finally { setBusyRow(null); }
  };

  const uploadFor = async (row, file) => {
    if (!file) return;
    setBusyRow(row.id);
    try {
      // Upload the file to the employee attachments scope, then attach
      // the resulting attachment_id + file_name to the doc row.
      const fd = new FormData();
      fd.append("file", file);
      const { data: att } = await api.post(
        `/attachments/employee/${userId}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/workspaces/${wsId}/employee-documents/${row.id}`, {
        attachment_id: att.id, file_name: att.filename,
      });
      toast.success("Uploaded");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setBusyRow(null); }
  };

  const remove = async (row) => {
    if (row.is_default) {
      // Instead of deleting the seed row, clear its attachment / verified.
      if (!window.confirm(`Detach the uploaded file from "${row.title}"?`)) return;
      patch(row.id, { attachment_id: null, file_name: null, verified: false });
      return;
    }
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    setBusyRow(row.id);
    try {
      await api.delete(`/workspaces/${wsId}/employee-documents/${row.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
    finally { setBusyRow(null); }
  };

  const addOther = async () => {
    const t = otherTitle.trim();
    if (!t) return;
    try {
      await api.post(`/workspaces/${wsId}/employee-documents`, {
        user_id: userId, category: "other", title: t,
      });
      toast.success("Added");
      setOtherTitle(""); setAddingOther(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
  };

  // Group rows by category (preserves backend sort — category, created_at)
  const grouped = state.rows.reduce((acc, r) => {
    (acc[r.category] = acc[r.category] || []).push(r);
    return acc;
  }, {});
  const catOrder = ["id_proof", "address_proof", "education", "past_work", "certificate", "other"];

  const summary = state.summary || {};

  return (
    <div className="space-y-4" data-testid="employee-documents-tab">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat label="Total rows" value={summary.total || 0} />
        <Stat label="Uploaded" value={`${summary.uploaded || 0} / ${summary.total || 0}`} sub={`${summary.uploaded_pct || 0}%`} tone={(summary.uploaded_pct || 0) === 100 ? "green" : (summary.uploaded_pct || 0) >= 50 ? "amber" : "red"} />
        <Stat label="Verified" value={`${summary.verified || 0} / ${summary.total || 0}`} sub={`${summary.verified_pct || 0}%`} tone={(summary.verified_pct || 0) === 100 ? "green" : (summary.verified_pct || 0) >= 50 ? "amber" : "red"} />
        <Stat label="Expiring soon" value={summary.expiring_soon || 0} tone={(summary.expiring_soon || 0) > 0 ? "amber" : "green"} />
        <Stat label="Expired" value={summary.expired || 0} tone={(summary.expired || 0) > 0 ? "red" : "green"} />
      </div>

      {state.loading ? (
        <div className="text-sm text-muted-foreground p-6 text-center">Loading documents…</div>
      ) : (
        catOrder.map((cat) => {
          const rows = grouped[cat] || [];
          if (rows.length === 0 && cat !== "other") return null;
          return (
            <div key={cat} className="border border-border bg-card" data-testid={`docs-category-${cat}`}>
              <div className="px-3 py-2 bg-muted/30 border-b border-border flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs uppercase tracking-widest font-semibold">{state.categories[cat] || cat}</span>
                <span className="text-[10px] text-muted-foreground">
                  {rows.filter((r) => r.attachment_id).length}/{rows.length} uploaded
                </span>
                {cat === "other" && !addingOther && (
                  <button type="button" onClick={() => setAddingOther(true)}
                          className="ml-auto text-[10px] uppercase tracking-widest text-primary hover:underline"
                          data-testid="docs-add-other-btn">
                    <Plus className="w-3 h-3 inline mr-1" /> Add doc
                  </button>
                )}
              </div>

              {cat === "other" && addingOther && (
                <div className="p-3 border-b border-border flex items-center gap-2" data-testid="docs-add-other-form">
                  <Input value={otherTitle} onChange={(e) => setOtherTitle(e.target.value)} placeholder="Document name" className="rounded-none h-8 text-xs" />
                  <Button size="sm" className="rounded-none" onClick={addOther} disabled={!otherTitle.trim()}>Add</Button>
                  <Button size="sm" variant="ghost" className="rounded-none" onClick={() => { setAddingOther(false); setOtherTitle(""); }}>Cancel</Button>
                </div>
              )}

              <div className="divide-y divide-border">
                {rows.map((r) => {
                  const busy = busyRow === r.id;
                  const isExpired = r.expiry_date && r.expiry_date < new Date().toISOString().slice(0, 10);
                  return (
                    <div key={r.id} className={`p-3 flex flex-wrap items-center gap-3 ${isExpired ? "bg-rose-50/40" : ""}`} data-testid={`docs-row-${r.id}`}>
                      <div className="flex-1 min-w-[16ch]">
                        <div className="text-sm font-semibold truncate">{r.title}</div>
                        {r.file_name && (
                          <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <Paperclip className="w-3 h-3" />
                            <a href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/v1/attachments/file/${r.attachment_id}/download`}
                               target="_blank" rel="noopener noreferrer"
                               className="hover:underline truncate max-w-[26ch]" data-testid={`docs-download-${r.id}`}>
                              {r.file_name}
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Verified toggle */}
                      <label className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] cursor-pointer ${r.verified ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-border text-muted-foreground hover:border-primary"}`}
                             data-testid={`docs-verified-label-${r.id}`}>
                        <input
                          type="checkbox"
                          checked={!!r.verified}
                          disabled={busy || !r.attachment_id}
                          onChange={(e) => patch(r.id, { verified: e.target.checked })}
                          className="h-3 w-3"
                          data-testid={`docs-verified-${r.id}`}
                        />
                        {r.verified
                          ? <><ShieldCheck className="w-3 h-3" /> Verified {r.verified_by_name ? `by ${r.verified_by_name}` : ""}</>
                          : <><ShieldAlert className="w-3 h-3" /> Not verified</>}
                      </label>

                      {/* Expiry date */}
                      <label className="inline-flex items-center gap-1 text-[10px]" title="Expiry date — flags row when within 30 days">
                        <Calendar className="w-3 h-3 text-muted-foreground" />
                        <input
                          type="date"
                          value={r.expiry_date || ""}
                          onChange={(e) => patch(r.id, { expiry_date: e.target.value || null })}
                          className="border border-border h-6 px-1 text-[10px] rounded-none bg-background"
                          data-testid={`docs-expiry-${r.id}`}
                          disabled={busy}
                        />
                        {isExpired && (
                          <span className="inline-flex items-center gap-1 text-rose-700 font-bold">
                            <AlertTriangle className="w-3 h-3" /> Expired
                          </span>
                        )}
                      </label>

                      {/* Upload */}
                      <input
                        type="file"
                        ref={(el) => { if (el) uploadRefs.current[r.id] = el; }}
                        onChange={(e) => uploadFor(r, e.target.files?.[0])}
                        className="hidden"
                        data-testid={`docs-file-input-${r.id}`}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none h-7 text-[10px]"
                        onClick={() => uploadRefs.current[r.id]?.click()}
                        disabled={busy}
                        data-testid={`docs-upload-btn-${r.id}`}
                      >
                        {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                        {r.attachment_id ? "Replace" : "Upload"}
                      </Button>
                      <button
                        type="button"
                        onClick={() => remove(r)}
                        className="text-muted-foreground hover:text-destructive p-1"
                        title={r.is_default ? "Detach uploaded file" : "Delete row"}
                        data-testid={`docs-remove-${r.id}`}
                        disabled={busy}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  const toneCls = tone === "green" ? "text-emerald-700"
                : tone === "amber" ? "text-amber-700"
                : tone === "red"   ? "text-rose-700"
                : "text-foreground";
  return (
    <div className="border border-border p-2 bg-background">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${toneCls}`}>{value}</div>
      {sub != null && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
